/**
 * 本地缓存加密层（AES-GCM）
 *
 * 设计目标：断网后无法查看聊天记录。
 * - 密钥由服务器在登录后下发（GET /user/dataKey），仅保存在内存中的 CryptoKey 变量里；
 * - 断网 / 关闭页面 / 刷新，内存密钥即丢失；
 * - IndexedDB 中的消息正文、会话最后消息等以密文形式存储，没有密钥无法解密；
 * - 只有重新联网、通过服务器认证后才能再次取到密钥并解密。
 *
 * 说明：Web 端无法做到绝对不可破解（HTTPS + WebCrypto 已是浏览器内最强实践），
 * 但“密钥只在内存、断网即丢”能确保断网/离线状态下本地记录不可读。
 */

const ENC_PREFIX = 'ENC1:'; // 密文标记，便于与历史明文缓存共存与识别

// 仅存于内存的密钥（CryptoKey），断网/刷新即丢
let cryptoKey = null;

const subtle = (typeof window !== 'undefined' && window.crypto && window.crypto.subtle)
	? window.crypto.subtle : null;

if (!subtle) {
	console.warn('当前环境不支持消息加密，本地缓存将以明文存储');
}

function b64ToBytes(b64) {
	const bin = atob(b64);
	const len = bin.length;
	const bytes = new Uint8Array(len);
	for (let i = 0; i < len; i++) {
		bytes[i] = bin.charCodeAt(i);
	}
	return bytes;
}

function bytesToB64(bytes) {
	let bin = '';
	const arr = new Uint8Array(bytes);
	for (let i = 0; i < arr.length; i++) {
		bin += String.fromCharCode(arr[i]);
	}
	return btoa(bin);
}

const dataCrypto = {
	/** 当前环境是否支持 WebCrypto（需 HTTPS 安全上下文） */
	isCryptoAvailable() {
		return !!subtle;
	},

	/** 是否已持有内存密钥（是否处于“已联网可读”状态） */
	hasKey() {
		return !!cryptoKey;
	},

	/** 用服务器下发的 base64 密钥导入内存 CryptoKey */
	async setKeyFromBase64(b64) {
		if (!subtle || !b64) {
			cryptoKey = null;
			return false;
		}
		try {
			const raw = b64ToBytes(b64);
			cryptoKey = await subtle.importKey('raw', raw, { name: 'AES-GCM' }, false, ['encrypt', 'decrypt']);
			return true;
		} catch (e) {
			console.error('导入本地数据密钥失败', e);
			cryptoKey = null;
			return false;
		}
	},

	/** 清除内存密钥（断网/锁屏/登出时调用） */
	clearKey() {
		cryptoKey = null;
	},

	/** 判断一个值是否为本层密文 */
	isEncrypted(val) {
		return typeof val === 'string' && val.indexOf(ENC_PREFIX) === 0;
	},

	/**
	 * 加密明文字符串；无密钥或空值时原样返回（写入通常发生在已联网、持有密钥时）
	 */
	async enc(plain) {
		if (!cryptoKey || !subtle) return plain;
		if (plain === null || plain === undefined || plain === '') return plain;
		if (this.isEncrypted(plain)) return plain; // 避免重复加密
		try {
			const iv = window.crypto.getRandomValues(new Uint8Array(12));
			const data = new TextEncoder().encode(String(plain));
			const cipher = await subtle.encrypt({ name: 'AES-GCM', iv }, cryptoKey, data);
			// 存储格式：ENC1:base64(iv):base64(cipher)
			return ENC_PREFIX + bytesToB64(iv) + ':' + bytesToB64(cipher);
		} catch (e) {
			console.error('本地数据加密失败', e);
			return plain;
		}
	},

	/**
	 * 解密；非密文原样返回（兼容历史明文）；无密钥或失败时返回占位符（断网不可读）
	 */
	async dec(val) {
		if (!this.isEncrypted(val)) return val; // 历史明文或非字符串
		if (!cryptoKey || !subtle) return '[需联网查看]';
		try {
			const parts = val.substring(ENC_PREFIX.length).split(':');
			const iv = b64ToBytes(parts[0]);
			const cipher = b64ToBytes(parts[1]);
			const plainBuf = await subtle.decrypt({ name: 'AES-GCM', iv }, cryptoKey, cipher);
			return new TextDecoder().decode(plainBuf);
		} catch (e) {
			return '[需联网查看]';
		}
	},

	/** 批量解密消息列表中的content字段（传输层解密，用于HTTP API返回的消息） */
	async decryptMessages(messages) {
		if (!Array.isArray(messages)) return messages;
		for (const m of messages) {
			if (m && typeof m.content === 'string' && this.isEncrypted(m.content)) {
				m.content = await this.dec(m.content);
			}
		}
		return messages;
	},

	/** 解密单条消息的content字段（传输层解密） */
	async decryptMessage(m) {
		if (m && typeof m.content === 'string' && this.isEncrypted(m.content)) {
			m.content = await this.dec(m.content);
		}
		return m;
	}
};

export default dataCrypto;
